
import "@progress/kendo-ui/js/kendo.colorpicker";
import "@progress/kendo-ui/js/kendo.combobox";
import "@progress/kendo-ui/js/kendo.datepicker";
import "@progress/kendo-ui/js/kendo.datetimepicker";
import "@progress/kendo-ui/js/kendo.slider";

import * as L from "leaflet";
import "leaflet.markercluster/dist/leaflet.markercluster.js";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.default.css";
import "leaflet/dist/leaflet.css";
import * as _ from "lodash";

import * as inputmask from "inputmask";
import * as ko from "knockout";
import * as Constants from "./constants";
import { getMapTileLayer } from "./mapUtil";
import * as UI from "./ui";
import { isNullOrSpace } from "./util";

export function registerKnockoutBindingHandlers() {

    ko.bindingHandlers.enterkey = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const callback = valueAccessor();
            $(element).keypress((event) => {
                const keyCode = (event.which ? event.which : event.keyCode);
                if (keyCode === 13) {
                    callback.call(null, bindingContext.$data);
                    return false;
                }
                return true;
            });
        },
    };

    ko.bindingHandlers.inputmask = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const el = $(element) as any;
            const boundValue = valueAccessor();
            el.val(boundValue.value());
            inputmask(boundValue.maskFormat).mask(el);
            const mask = el[0].inputmask;

            el.on("focusout change", () => {
                if (mask.isComplete()) {
                    boundValue.value(mask.unmaskedvalue());
                } else {
                    boundValue.value(null);
                }
            });
            $(element).attr("placeholder", boundValue.maskFormat);
        },
    };

    ko.bindingHandlers.singleCheckboxAnswer = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const selected = valueAccessor().value();
            if (isNullOrSpace(selected)) {
                valueAccessor().value(Constants.SINGLECHECKBOX_ANSWER_FALSE);
            }
            const el = $(element) as any;
            el.on("change", () => {
                if (valueAccessor().value() === "true") {
                     valueAccessor().value(Constants.SINGLECHECKBOX_ANSWER_FALSE);
                     valueAccessor().checked(false);
                } else {
                    valueAccessor().value(Constants.SINGLECHECKBOX_ANSWER_TRUE);
                    valueAccessor().checked(true);
               }
            });
        },
    };

    ko.bindingHandlers.colorpicker = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const el = $(element) as any;
            const boundValue = valueAccessor();

            // initializing the color picker for the list
            el.kendoColorPicker({
                value: boundValue.value(),
                buttons: true,
                select: (e: kendo.ui.ColorPickerSelectEvent) => {
                    boundValue.value(e.value);
                },

            });

        },
    };

    ko.bindingHandlers.combobox = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const el = $(element) as any;
            const boundValue = valueAccessor();

            // initializing the color picker for the list
            el.kendoComboBox({
                value: boundValue.value(),
                dataSource: boundValue.dataSource(),
                dataTextField: boundValue.dataTextField,
                dataValueField: boundValue.dataValueField,
                filter: "contains",
                select: (e: kendo.ui.ComboBoxSelectEvent) => {
                    if (e.dataItem) {
                        boundValue.value(e.dataItem[boundValue.dataValueField]);
                    }
                },
                change: (e: kendo.ui.ComboBoxChangeEvent) => {
                    if (!el.val()) {
                        boundValue.value("");
                    }
                },
            });

        },
    };

    ko.bindingHandlers.date = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).kendoDatePicker({
                format: UI.getKendoDateFormat(valueAccessor().dateFormat),
            });
            $(element).attr("placeholder", valueAccessor().dateFormat);
        },
    };

    ko.bindingHandlers.datetime = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).kendoDateTimePicker({
                format: UI.getKendoDateFormat(valueAccessor().dateFormat) + " " + UI.getKendoTimeFormat(valueAccessor().timeFormat),
                timeFormat: UI.getKendoTimeFormat(valueAccessor().timeFormat),
            });
            $(element).attr("placeholder", valueAccessor().dateFormat + " " + Constants.TIME_PLACEHOLDER);
        },
    };

    ko.bindingHandlers.time = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).kendoTimePicker({
                format: UI.getKendoTimeFormat(valueAccessor().timeFormat),
            });
            $(element).attr("placeholder", Constants.TIME_PLACEHOLDER);
        },
    };

    ko.bindingHandlers.numeric = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).kendoNumericTextBox({
                format: UI.getKendoNumericFormat(valueAccessor().numberFormat, valueAccessor().numberOfDecimals),
                restrictDecimals: true,
                decimals: valueAccessor().numberOfDecimals,
                min: valueAccessor().min === "" ? null : valueAccessor().min,
                max: valueAccessor().max === "" ? null : valueAccessor().max,
            });
            $(element).attr("placeholder", Constants.NUMERIC_PLACEHOLDER);

            const control = $(element).data("kendoNumericTextBox");
            if (!valueAccessor().readonly) {
                control.readonly(false);
            } else {
                control.readonly(true);
            }
        },
    };

    ko.bindingHandlers.formItemMap = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
           updateMap(valueAccessor, element);
        },
        update(element, valueAccessor, viewModel) {
            updateMap(valueAccessor, element);
        },

    };
    function updateMap( valueAccessor: any, element: any) {
        const lat: number = parseFloat(valueAccessor().latitude());
        const long: number = parseFloat(valueAccessor().longitude());
        let map: L.Map;
        const tiles: L.TileLayer = getMapTileLayer();
        $(element).empty();
        $(element).html("<div class='map'></div>");
        map = L.map($(element).find("div")[0], { layers: [tiles] });
        const iconHtml: string = "<i class=\"fa fa-map-marker fa-3x\"></i>";

        valueAccessor().latitude(isNaN(lat) ? null : lat);
        valueAccessor().longitude( isNaN(long) ? null : long);
        // ValidLat , ValidLong are used to validate the user input over numeric co-ordinates
        valueAccessor().validLat(validateLatLong(valueAccessor().latitude(), true));
        valueAccessor().validLong(validateLatLong(valueAccessor().longitude(), false));

        if ( !_.isNaN(lat) && !_.isNaN(long) && valueAccessor().validLat() && valueAccessor().validLong()) {
                map.setView([lat , long], 10);
                const marker: L.Marker = L.marker (L.latLng(lat, long), {
                    title: "current location",
                    icon: new L.DivIcon({ html: iconHtml, className: "map-icon", iconSize: [30, 35] }),
                    draggable: true,
                });
                marker.on("dragend", (event: L.LeafletEvent) => {
                    UI.showConfirmDialog(" Are you sure you want to move the location?", () => {
                        const location = marker.getLatLng();
                        valueAccessor().latitude(location.lat);
                        valueAccessor().longitude(location.lng);
                    },
                    () => {
                        marker.setLatLng(L.latLng( lat, long));
                    });
                });
                marker.addTo(map);
                setTimeout(() => {
                    map.invalidateSize();
                }, 100);
                valueAccessor().hasValue(true);
                valueAccessor().latLongMissing(false);
        }

    }

    function validateLatLong(latLong: number , isLatitude: boolean): boolean {
        let valid: boolean = true;
        if (!_.isNaN(latLong) && !_.isNull(latLong) ) {
            if (isLatitude) {
                valid = (latLong >= -90 && latLong <= 90 ) ? valid : false;
            } else {
                valid = (latLong >= -180 && latLong <= 180 ) ? valid : false;
            }
        }
        return valid;
    }

    ko.bindingHandlers.slider = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            const minVal = parseFloat(valueAccessor().min);
            const maxVal = parseFloat(valueAccessor().max);
            const defaultValue = parseFloat(valueAccessor().defaultValue);
            const selectVal = parseFloat(valueAccessor().value()) ? valueAccessor().value() : defaultValue;
            const interval = parseFloat(valueAccessor().interval);
            valueAccessor().value(selectVal);
            $(element).kendoSlider({
                value: selectVal,
                min: minVal,
                max: maxVal,
                smallStep: interval,
                largeStep: interval,
                change: (e: kendo.ui.SliderChangeEvent) => {
                    valueAccessor().value(e.value);
                },
                slide: (e: kendo.ui.SliderSlideEvent) => {
                    valueAccessor().value(e.value);
                },

            });
            const slider = $(element).data("kendoSlider");
            slider.enable(!valueAccessor().readOnly);
        },
    };

    // this is to display slider after we save TemplateFormItems.
    ko.bindingHandlers.templateSlider = {
        init(element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).kendoSlider({
                value: valueAccessor().value,
                min: valueAccessor().min,
                max: valueAccessor().mix,
                tickPlacement: "none",
                tooltip: {
                    enabled: false,
               },
            });
        },
    };

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/knockoutBindingHandlers.ts